/**
 * AGE RULES ENGINE - SINGLE SOURCE OF TRUTH
 * 
 * This module defines the age calculation and rule logic that MUST be
 * identical between frontend and backend. Any changes here must be
 * mirrored in the backend (server.py calculate_age function).
 * 
 * RULE EXECUTION ORDER:
 * 1. Minimum age check -> block if under
 * 2. Over-age rule -> apply configured action if age >= threshold
 * 3. Bank validation -> only if bank fields are active
 */

// ==================== AGE CALCULATION (SINGLE SOURCE OF TRUTH) ====================

/**
 * Calculate age from date of birth components
 * Handles: birthday boundary, leap years, invalid dates
 * 
 * @param {number|string} day - Day of birth (1-31)
 * @param {number|string} month - Month of birth (1-12)
 * @param {number|string} year - Year of birth (4-digit)
 * @returns {number|null} - Age in years, or null if invalid
 */
export function calculateAge(day, month, year) {
  if (!day || !month || !year) return null;
  
  const d = parseInt(day);
  const m = parseInt(month);
  const y = parseInt(year);
  
  if (isNaN(d) || isNaN(m) || isNaN(y)) return null;
  if (d < 1 || d > 31 || m < 1 || m > 12 || y < 1900 || y > new Date().getFullYear()) return null;
  
  const today = new Date();
  const birthDate = new Date(y, m - 1, d);
  
  // Validate date is real (handles Feb 30, April 31, etc.)
  if (birthDate.getDate() !== d || birthDate.getMonth() !== m - 1 || birthDate.getFullYear() !== y) {
    return null;
  }
  
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  
  // If birthday hasn't occurred yet this year, subtract 1
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  
  return age;
}

// ==================== AGE RULE ACTIONS ====================

export const AGE_RULE_ACTIONS = {
  ALLOW: 'allow',
  BLOCK: 'block',
  CALLBACK: 'callback',
  SELF_COMPLETE: 'self_complete',
  CALLBACK_OR_SELF_COMPLETE: 'callback_or_self_complete'
};

export const AGE_RULE_RESULTS = {
  VALID: 'valid',           // Normal flow allowed
  UNDER_AGE: 'under_age',   // Below minimum age - hard block
  BLOCK: 'block',           // Over-age block action
  CALLBACK: 'callback',     // Over-age callback flow
  SELF_COMPLETE: 'self_complete',  // Over-age self-completion
  CALLBACK_OR_SELF_COMPLETE: 'callback_or_self_complete'  // User choice
};

// ==================== RULE EVALUATION ====================

/**
 * Evaluate age rules based on campaign configuration
 * This logic MUST match backend exactly
 * 
 * @param {number|null} age - Calculated age
 * @param {object} campaignConfig - Campaign configuration
 * @returns {object} - { result, message, isBankDisabled, isSubmitBlocked }
 */
export function evaluateAgeRules(age, campaignConfig) {
  const minAge = campaignConfig?.minAge ?? 18;
  const overAgeThreshold = campaignConfig?.overAgeThreshold ?? 80;
  const overAgeAction = campaignConfig?.overAgeAction ?? AGE_RULE_ACTIONS.ALLOW;
  
  // Default result
  const result = {
    result: AGE_RULE_RESULTS.VALID,
    action: null,
    message: null,
    isBankDisabled: false,
    isSubmitBlocked: false,
    requiresCallback: false,
    requiresSelfComplete: false,
    showWarning: false
  };
  
  // No age yet - can't evaluate
  if (age === null) {
    return result;
  }
  
  // RULE 1: Minimum age check (HARD BLOCK)
  if (age < minAge) {
    return {
      ...result,
      result: AGE_RULE_RESULTS.UNDER_AGE,
      action: 'block',
      message: `You must be at least ${minAge} years old to sign up.`,
      isSubmitBlocked: true,
      showWarning: true
    };
  }
  
  // RULE 2: Over-age threshold (age >= threshold triggers action)
  // CRITICAL: Must use >= comparison, not >
  if (age >= overAgeThreshold) {
    switch (overAgeAction) {
      case AGE_RULE_ACTIONS.ALLOW:
        // No restrictions
        return result;
        
      case AGE_RULE_ACTIONS.BLOCK:
        return {
          ...result,
          result: AGE_RULE_RESULTS.BLOCK,
          action: 'block',
          message: 'Online signup is not available for your age range. Please contact us directly.',
          isSubmitBlocked: true,
          showWarning: true
        };
        
      case AGE_RULE_ACTIONS.CALLBACK:
        return {
          ...result,
          result: AGE_RULE_RESULTS.CALLBACK,
          action: 'callback',
          message: 'For your protection, a team member will contact you to complete your signup.',
          isBankDisabled: true,
          requiresCallback: true,
          showWarning: true
        };
        
      case AGE_RULE_ACTIONS.SELF_COMPLETE:
        return {
          ...result,
          result: AGE_RULE_RESULTS.SELF_COMPLETE,
          action: 'self_complete',
          message: 'Please complete this form on your own device using the referral link.',
          isSubmitBlocked: true,
          requiresSelfComplete: true,
          showWarning: true
        };
        
      case AGE_RULE_ACTIONS.CALLBACK_OR_SELF_COMPLETE:
        return {
          ...result,
          result: AGE_RULE_RESULTS.CALLBACK_OR_SELF_COMPLETE,
          action: 'choice',
          message: 'Please choose how you would like to proceed.',
          // Submit blocked until choice is made
          isSubmitBlocked: true,
          showWarning: true
        };
        
      default:
        return result;
    }
  }
  
  return result;
}

/**
 * Re-evaluate rules after user makes a choice (for callback_or_self_complete)
 */
export function evaluateWithChoice(age, campaignConfig, selectedOption) {
  const baseResult = evaluateAgeRules(age, campaignConfig);
  
  if (baseResult.result !== AGE_RULE_RESULTS.CALLBACK_OR_SELF_COMPLETE) {
    return baseResult;
  }
  
  if (!selectedOption) {
    return baseResult; // Still blocked, waiting for choice
  }
  
  if (selectedOption === 'callback') {
    return {
      ...baseResult,
      action: 'callback',
      isBankDisabled: true,
      isSubmitBlocked: false,
      requiresCallback: true
    };
  }
  
  if (selectedOption === 'self_complete') {
    return {
      ...baseResult,
      action: 'self_complete',
      isSubmitBlocked: true,
      requiresSelfComplete: true
    };
  }
  
  return baseResult;
}

// ==================== FORM STATE VALIDATION ====================

/**
 * Validate form state consistency after DOB change
 * Returns fields that should be cleared/disabled
 */
export function getFieldsToReconcile(prevAge, newAge, campaignConfig, formData) {
  const prevRules = evaluateAgeRules(prevAge, campaignConfig);
  const newRules = evaluateAgeRules(newAge, campaignConfig);
  
  const reconciliation = {
    clearBankDetails: false,
    clearSignature: false,
    resetAgeOption: false
  };
  
  // If switching TO callback/self-complete flow, clear bank details
  if (!prevRules.isBankDisabled && newRules.isBankDisabled) {
    reconciliation.clearBankDetails = true;
  }
  
  // If switching to blocked state, clear signature
  if (!prevRules.isSubmitBlocked && newRules.isSubmitBlocked) {
    reconciliation.clearSignature = true;
  }
  
  // If rule result changed, reset the age option selection
  if (prevRules.result !== newRules.result) {
    reconciliation.resetAgeOption = true;
  }
  
  return reconciliation;
}

// ==================== PAYMENT FREQUENCY ====================

/**
 * Get available payment frequencies based on campaign config
 */
export function getPaymentFrequencies(campaignConfig, conversionType) {
  const enableAnnual = campaignConfig?.enableAnnualPayments ?? false;
  
  // Only for lottery and regular_giving
  if (conversionType !== 'lottery' && conversionType !== 'regular_giving') {
    return [{ value: 'monthly', label: 'Monthly' }];
  }
  
  if (!enableAnnual) {
    return [{ value: 'monthly', label: 'Monthly' }];
  }
  
  return [
    { value: 'monthly', label: 'Monthly' },
    { value: 'annual', label: 'Annual' }
  ];
}

/**
 * Calculate display price based on frequency
 */
export function getDisplayPrice(basePrice, frequency) {
  if (frequency === 'annual') {
    return basePrice * 12;
  }
  return basePrice;
}
